import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  applyAccent,
  applyTheme,
  normalizeThemePreference,
  themeStorageKey,
  type AccentProfile,
  type ThemePreference,
} from "./theme-preference";

type ThemeContextValue = {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
};
const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  accent = "blue",
}: {
  children: ReactNode;
  accent?: AccentProfile;
}) {
  const [theme, setTheme] = useState<ThemePreference>(() =>
    normalizeThemePreference(localStorage.getItem(themeStorageKey)),
  );
  useEffect(() => applyAccent(accent), [accent]);
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    function update() {
      applyTheme(theme);
    }
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [theme]);
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return value;
}
